import { useState, useEffect } from 'react'
import { useQueryClient, QueryObserver } from '@tanstack/react-query'

const KEY = 'REMOVED_DATASET_ROWS'

/**
 * A custom hook that observes removed rows for a specific dataset.
 * The removed rows are managed in the React Query cache, and this hook provides utilities
 * to fetch, observe, and update these rows.
 *
 * @returns {object} Object containing the current removed rows and a function to add new removed rows.
 * @returns {string[]} .removedRows The current set of removed rows for the dataset.
 * @returns {function} .addRemovedRows A function that accepts an array of rows and adds them to the removed rows.
 *
 * @example
 * const { removedRows, addRemovedRows } = useDatasetRemovedRowsObserver();
 *
 * // Add new removed rows
 * addRemovedRows(['row1', 'row2']);
 */
export const useDatasetRemovedRowsObserver = ({ datasetId }) => {
  const queryClient = useQueryClient()

  const [removedRows, setRemovedRows] = useState<string[]>(() => {
    const data = queryClient.getQueryData<string[]>([KEY, datasetId])

    return data ?? []
  })

  const fetchRemovedRows = () => queryClient.getQueryData<string[]>([KEY, datasetId]) ?? []

  useEffect(() => {
    const removedObserver = new QueryObserver<string[]>(queryClient, {
      queryKey: [KEY, datasetId],
      queryFn: fetchRemovedRows,
    })

    const unsubscribeRemoved = removedObserver.subscribe(result => {
      if (result.data?.length) setRemovedRows(result.data)
    })

    return () => {
      unsubscribeRemoved()
    }
  }, [])

  const addRemovedRows = (newRows: string[]) => {
    // Get the current data from the query client
    const currentData = queryClient.getQueryData<string[]>([KEY, datasetId]) ?? []

    // Merge current data with new rows
    const updatedData = [...currentData, ...newRows]

    // Set the merged data back into the query client
    queryClient.setQueryData([KEY, datasetId], updatedData)

    // Update the local state as well
    setRemovedRows(updatedData)
  }

  return {
    removedRows,
    addRemovedRows,
  }
}
